/** Replay pending outbox ops over freshly-fetched server state.
 *
 * While the outbox holds unsent edits, the server copy is BEHIND the user:
 * a fetch must never visually revert what they just did (the v1 "my edit
 * vanished, then reappeared" flicker). These pure functions layer the queue
 * back over fetched state; when the ops land server-side, fetch and replay
 * converge to the same picture by construction.
 */
import type { RecOp } from "../../lib/outbox";
import type { EditsMap, LeadFields, TaskRec } from "./api";

const merge = (a: LeadFields, b: Record<string, unknown>, rep?: Set<string>): LeadFields => {
  const out: LeadFields = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const prev = out[k];
    if (rep?.has(k)) {
      // Phase 12 replace-keys: the value lands verbatim; null removes the key
      if (v === null) delete out[k];
      else out[k] = v;
    } else if (
      v !== null && typeof v === "object" && !Array.isArray(v) &&
      prev !== null && typeof prev === "object" && !Array.isArray(prev)
    ) {
      out[k] = merge(prev as LeadFields, v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
};


/** Op payloads are objects for put/patch; calendar's group op carries an
 * ARRAY (never replayed here) and settings.SHOWINGTIME_FEED a STRING (also
 * never replayed here — only lead_fields ops reach this). Narrow to object. */
const dObj = (d: RecOp["d"]): Record<string, unknown> =>
  d !== null && d !== undefined && typeof d === "object" && !Array.isArray(d) ? d : {};

export function applyOpsToEdits(base: EditsMap, ops: RecOp[]): EditsMap {
  const map: EditsMap = new Map(base);
  for (const op of ops) {
    if (op.s !== "lead_fields") continue;
    if (op.op === "del") {
      map.delete(op.k);
    } else if (op.op === "put") {
      map.set(op.k, { ...dObj(op.d) });
    } else {
      map.set(op.k, merge(map.get(op.k) ?? {}, dObj(op.d), op.rep?.length ? new Set(op.rep) : undefined));
    }
  }
  return map;
}

export interface DealLike {
  natKey: string;
  stageIdx: number;
  fields: Record<string, unknown>;
  // Internal Assignment mirrors (narrowDeal reads these off the card's TOP LEVEL,
  // not fields) — optional so minimal fixtures/callers still satisfy DealLike.
  label?: string | null;
  partner?: string | null;
  partnerType?: string | null;
  partnerColor?: string | null;
  partnerNote?: string | null;
  /** CRM-side Drive file names (2026-09-07) */
  driveLabels?: Record<string, string>;
}

/** narrowDeal's string coercion (trim; empty → null) — kept local so the
 * optimistic replay narrows label/partner exactly like the real read. */
const dealStr = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** Replay pending deals ops over fetched deals: stage moves land, field
 * edits (d.edits) merge into fields — mirrors rec_apply_ops_core semantics. */
export function applyOpsToDeals<T extends DealLike>(base: T[], ops: RecOp[]): T[] {
  let deals = [...base];
  for (const op of ops) {
    if (op.s !== "deals") continue;
    if (op.op === "del") {
      deals = deals.filter((d) => d.natKey !== op.k);
      continue;
    }
    const idx = deals.findIndex((d) => d.natKey === op.k);
    if (idx < 0) continue; // v2 doesn't create deals yet; skip unknown keys
    const cur = deals[idx];
    if (!cur) continue;
    const d = dObj(op.d);
    const edits = d["edits"];
    const next: T = {
      ...cur,
      stageIdx: op.stage !== undefined ? op.stage : cur.stageIdx,
      fields:
        edits !== null && typeof edits === "object" && !Array.isArray(edits)
          ? merge(cur.fields, edits as Record<string, unknown>)
          : cur.fields,
    };
    // Internal Assignment (opAssignDeal) writes label/partner at the card's TOP
    // LEVEL — replay them so a background refresh that lands before the op drains
    // doesn't flicker the pill back to the (stale) server value.
    if ("label" in d) next.label = dealStr(d["label"]);
    if ("partner" in d) {
      const p = d["partner"];
      const po = p !== null && typeof p === "object" && !Array.isArray(p) ? (p as Record<string, unknown>) : null;
      next.partner = po ? dealStr(po["name"]) : null;
      next.partnerType = po ? dealStr(po["type"]) : null;
      next.partnerColor = po ? dealStr(po["color"]) : null;
      next.partnerNote = po ? dealStr(po["note"]) : null;
    }
    // CRM-side Drive file names (opDealDriveLabel) — a nested merge, null clears
    if ("driveLabels" in d && d["driveLabels"] !== null && typeof d["driveLabels"] === "object") {
      const merged: Record<string, string> = { ...(cur.driveLabels ?? {}) };
      for (const [fid, lab] of Object.entries(d["driveLabels"] as Record<string, unknown>)) {
        if (typeof lab === "string" && lab.trim()) merged[fid] = lab.trim();
        else delete merged[fid];
      }
      next.driveLabels = merged;
    }
    deals[idx] = next;
  }
  return deals;
}

export function applyOpsToTasks(base: TaskRec[], ops: RecOp[]): TaskRec[] {
  let tasks = [...base];
  const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
  for (const op of ops) {
    if (op.s !== "tasks") continue;
    if (op.op === "del") {
      tasks = tasks.filter((t) => t.natKey !== op.k);
      continue;
    }
    const d = dObj(op.d);
    const idx = tasks.findIndex((t) => t.natKey === op.k);
    if (op.op === "put") {
      const next: TaskRec = {
        natKey: op.k,
        title: typeof d["t"] === "string" && d["t"].trim() ? (d["t"] as string).trim() : op.k.split("|")[0] ?? op.k,
        status: str(d["st"]),
        who: str(d["who"]),
        due: str(d["due"]),
        pri: str(d["pri"]),
        deal: str(d["deal"]),
        stage: str(d["stage"]),
        auto: d["auto"] === true,
      };
      if (idx >= 0) tasks[idx] = next;
      else tasks.unshift(next);
    } else if (idx >= 0) {
      const cur = tasks[idx];
      if (!cur) continue;
      tasks[idx] = {
        ...cur,
        status: d["st"] !== undefined ? str(d["st"]) : cur.status,
        who: d["who"] !== undefined ? str(d["who"]) : cur.who,
        due: d["due"] !== undefined ? str(d["due"]) : cur.due,
        pri: d["pri"] !== undefined ? str(d["pri"]) : cur.pri,
      };
    }
  }
  return tasks;
}
